import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { BookOpen, ArrowUpRight, Sparkle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

const STORAGE_KEY = "fb_book_popup_seen";
const COOLDOWN_MS = 24 * 60 * 60 * 1000; // 24h
const OPEN_DELAY_MS = 3000;
const BOOK = {
  title: "Dialogue avec l'enfant en soi",
  author: "Francine Banzadio",
  tagline: "Un chemin vers sa liberté intérieure",
  release: "22 juin 2026",
  cover:
    "https://customer-assets.emergentagent.com/job_francine-schemas/artifacts/hnwk10ng_81q9lxUWsLL._SY342_.jpg",
  buyUrl:
    "https://www.amazon.fr/DIALOGUE-AVEC-ENFANT-EN-SOI/dp/2322859613",
  buyUrlFnac:
    "https://www.fnac.com/a23346638/Francine-Banzadio-Dialogue-avec-enfant-en-soi",
  illustrator: "Ambre Loiseau",
};

export default function BookAnnouncement() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    if (location.pathname.startsWith("/admin")) return;

    let seenAt = 0;
    try {
      const v = localStorage.getItem(STORAGE_KEY);
      seenAt = v ? Number(v) : 0;
    } catch {
      seenAt = 0;
    }
    if (Date.now() - seenAt < COOLDOWN_MS) return;

    const timer = setTimeout(() => setOpen(true), OPEN_DELAY_MS);
    return () => clearTimeout(timer);
  }, [location.pathname]);

  const dismiss = () => {
    try {
      localStorage.setItem(STORAGE_KEY, String(Date.now()));
    } catch { /* ignore */ }
    setOpen(false);
  };

  const openBook = (url) => {
    try {
      localStorage.setItem(STORAGE_KEY, String(Date.now()));
    } catch { /* ignore */ }
    window.open(url, "_blank", "noopener,noreferrer");
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? setOpen(true) : dismiss())}>
      <DialogContent
        data-testid="book-popup"
        className="sm:max-w-2xl bg-[#FAF7F8] border-[#DDD7E2] rounded-3xl p-0 overflow-hidden max-h-[92vh] overflow-y-auto"
      >
        <div className="grid md:grid-cols-5">
          {/* Cover */}
          <div className="md:col-span-2 relative bg-gradient-to-br from-[#3A2E6E] via-[#3A2E6E] to-[#2847B8] p-6 md:p-8 flex items-center justify-center">
            <div className="absolute inset-0 pointer-events-none">
              <div className="absolute -top-10 -left-10 w-40 h-40 rounded-full bg-[#C98FA3]/25 blur-3xl" />
              <div className="absolute -bottom-10 -right-10 w-40 h-40 rounded-full bg-[#E6D6DF]/25 blur-3xl" />
            </div>
            <img
              src={BOOK.cover}
              alt={`Couverture du livre ${BOOK.title}`}
              data-testid="book-popup-cover"
              className="relative w-40 md:w-52 rounded-lg shadow-[0_30px_60px_-20px_rgba(0,0,0,0.5)]"
            />
          </div>

          {/* Content */}
          <div className="md:col-span-3 p-7 md:p-9 relative">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#F3EEF4] border border-[#C98FA3]/30 text-xs uppercase tracking-[0.22em] text-[#3A2E6E] font-medium">
              <Sparkle className="w-3 h-3 text-[#C98FA3]" />
              Nouveauté
            </div>

            <DialogTitle asChild>
              <h2 className="font-serif-display text-3xl md:text-4xl font-light text-[#1E1E2A] leading-[1.1] mt-4">
                {BOOK.title}
              </h2>
            </DialogTitle>

            <p className="mt-2 text-sm uppercase tracking-[0.2em] text-[#5F6170] font-medium">
              {BOOK.author}
            </p>

            <DialogDescription asChild>
              <p className="mt-5 font-serif-display italic text-lg text-[#3A2E6E] leading-snug">
                « {BOOK.tagline} »
              </p>
            </DialogDescription>

            <div className="mt-6 flex flex-wrap items-center gap-3 text-xs text-[#5F6170]">
              <span className="inline-flex items-center gap-1.5">
                <BookOpen className="w-3.5 h-3.5 text-[#C98FA3]" />
                Parution : {BOOK.release}
              </span>
              <span className="text-[#DDD7E2]">·</span>
              <span>Illustrations : {BOOK.illustrator}</span>
            </div>

            <div className="mt-8 space-y-3">
              <p className="text-[10px] uppercase tracking-[0.22em] text-[#5F6170] font-medium">
                Disponible chez
              </p>
              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  onClick={() => openBook(BOOK.buyUrl)}
                  data-testid="book-popup-cta-amazon"
                  className="group flex-1 inline-flex items-center justify-between gap-2 bg-[#2847B8] text-white hover:bg-[#3A2E6E] transition-all duration-300 rounded-full px-5 py-3 font-medium text-sm"
                >
                  Amazon
                  <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </button>
                <button
                  onClick={() => openBook(BOOK.buyUrlFnac)}
                  data-testid="book-popup-cta-fnac"
                  className="group flex-1 inline-flex items-center justify-between gap-2 bg-white border-2 border-[#3A2E6E] text-[#3A2E6E] hover:bg-[#3A2E6E] hover:text-white transition-all duration-300 rounded-full px-5 py-3 font-medium text-sm"
                >
                  Fnac
                  <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </button>
              </div>
              <button
                onClick={dismiss}
                data-testid="book-popup-later"
                className="text-xs text-[#5F6170] hover:text-[#3A2E6E] transition-colors pt-1"
              >
                Peut-être plus tard
              </button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
